import { EAppEnv } from 'affiny-core/env';
import { EEnvCountry, EEnvLocale } from 'affiny-core/env.types';
import { isLocalhost } from 'affiny-core/services/hostService';
import { EBrand, EHostLang, EUrlEnv } from 'affiny-core/types/env.types';

declare const process: {
  env: {
    REACT_APP_TOP_LEVEL_DOMAIN: EHostLang;
    REACT_APP_BRAND: EBrand;
    REACT_APP_SUBDOMAIN: string;
  };
};

export interface IURLContext {
  hostLang: EHostLang;
  country: EEnvCountry;
  locale: EEnvLocale;
  envName: Exclude<EAppEnv, EAppEnv.test>;
  brand: EBrand;
  namespace?: string;
}

export const parseHostUrl = (host: string): IURLContext => {
  const matches = host.match(
    /^(?:(?<namespace>[a-zA-Z]*)\.)?(?<subdomain>nl|fr|www)\.(?<brand>affiny|partner|meeticaffinity|matchaffinity|lexamore)\.(?<tld>fr|co\.uk|de|it|es|pt|no|se|dk|nl|fi|be|ch|ie)(?:\.(?:(?!kube\.recette\.ilius\.io|pprod|recette)\w*))*(?:\.(?<env>kube\.recette\.ilius\.io|pprod|recette))?/
  );

  if (!matches) {
    const topLevelDomain = process.env.REACT_APP_TOP_LEVEL_DOMAIN;

    return {
      hostLang: topLevelDomain,
      country: computeCountryFromHostLang(
        topLevelDomain,
        process.env.REACT_APP_BRAND
      ),
      locale: computeLocaleFromHostLang(
        process.env.REACT_APP_SUBDOMAIN === 'www'
          ? topLevelDomain
          : (process.env.REACT_APP_SUBDOMAIN as EHostLang),
        process.env.REACT_APP_BRAND
      ),
      brand: process.env.REACT_APP_BRAND,
      envName: isLocalhost(host) ? EAppEnv.local : EAppEnv.staging,
    };
  }

  const namespace = matches[1];
  const urlEnv = matches[5] as EUrlEnv;
  const hostLang = matches[4] as EHostLang;

  return {
    hostLang,
    country: computeCountryFromHostLang(hostLang, matches[3] as EBrand),
    locale: computeLocaleFromHostLang(
      matches[2] === 'www' ? hostLang : (matches[2] as EHostLang),
      matches[3] as EBrand
    ),
    namespace,
    brand: matches[3] as EBrand,
    envName: computeEnvName({ urlEnv, host, namespace }),
  };
};

export const computeCountryFromHostLang = (
  hostLang?: EHostLang,
  brand?: EBrand
): EEnvCountry => {
  switch (hostLang) {
    case EHostLang.UK:
      return EEnvCountry.EN;
    case EHostLang.IE:
      return EEnvCountry.IE;
    case EHostLang.FR:
      return EEnvCountry.FR;
    case EHostLang.CH:
      return EEnvCountry[brand === 'partner' ? 'CHG' : 'CHF'];
    case EHostLang.BE:
      return EEnvCountry.BE;
    case EHostLang.DE:
      return EEnvCountry.DE;
    case EHostLang.IT:
      return EEnvCountry.IT;
    case EHostLang.ES:
      return EEnvCountry.ES;
    case EHostLang.DK:
      return EEnvCountry.DK;
    case EHostLang.NO:
      return EEnvCountry.NO;
    case EHostLang.PT:
      return EEnvCountry.PT;
    case EHostLang.NL:
      return EEnvCountry.NL;
    case EHostLang.SE:
      return EEnvCountry.SE;
    case EHostLang.FI:
      return EEnvCountry.FI;
    default:
      return EEnvCountry.FR;
  }
};

export const computeLocaleFromHostLang = (
  hostLang?: EHostLang,
  brand?: EBrand
): EEnvLocale => {
  switch (hostLang) {
    case EHostLang.UK:
    case EHostLang.IE:
      return EEnvLocale.en;
    case EHostLang.FR:
      return EEnvLocale.fr;
    case EHostLang.CH:
      return EEnvLocale[brand === 'partner' ? 'de' : 'fr'];
    case EHostLang.DE:
      return EEnvLocale.de;
    case EHostLang.IT:
      return EEnvLocale.it;
    case EHostLang.ES:
      return EEnvLocale.es;
    case EHostLang.DK:
      return EEnvLocale.da;
    case EHostLang.NO:
      return EEnvLocale.nb;
    case EHostLang.PT:
      return EEnvLocale.pt;
    case EHostLang.NL:
      return EEnvLocale.nl;
    case EHostLang.SE:
      return EEnvLocale.sv;
    case EHostLang.FI:
      return EEnvLocale.fi;
    default:
      return EEnvLocale.fr;
  }
};

const computeEnvName = ({
  urlEnv,
  host,
  namespace,
}: {
  urlEnv: EUrlEnv;
  host: string;
  namespace: string;
}): Exclude<EAppEnv, EAppEnv.test> => {
  switch (urlEnv) {
    case EUrlEnv.LOCAL:
      return EAppEnv.local;
    case EUrlEnv.STAGING:
      return EAppEnv.staging;
    case EUrlEnv.KUBERECETTE:
      return EAppEnv.kuberecette;
    case EUrlEnv.PPROD:
      return EAppEnv.preprod;
    default:
      if (!namespace) {
        return EAppEnv.prod;
      }

      return EAppEnv.staging;
  }
};
